import { useQueryClient } from '@tanstack/react-query';
import { ShirtIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';

import { SkuType, type SkuAssemblyComponentDTO, type SkuDTO } from '@invenco/products-interface';

import { DetailGroup } from '@/components/detail-group';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderDescription, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';

import { AssembliesCard } from './assemblies-card';
import { ComponentsTable } from './components-table';
import { DetailsSection } from './details-section';
import { InventoryTable } from './inventory-table';
import { ListingsTable } from './listings-table';
import { MeasurementsSection } from './measurements-section';
import { MysteryComponentManagementDialog } from './mystery-component-management-dialog';
import { MysteryComponentsCard } from './mystery-components-card';
import { PricesSection } from './prices-section';
import { SkuActions } from './sku-actions';
import { UnitsTable } from './units-table';
import { type MysteryComponent, useMysteryComponents } from './use-mystery-components';

export function SkuDetails() {
  const { id } = useParams<{ id: string }>();
  const { productsGateway } = useGateways();
  const queryClient = useQueryClient();
  const [editingMysteryComponent, setEditingMysteryComponent] = useState<
    MysteryComponent | 'new' | undefined
  >();
  const {
    data: sku,
    isLoading,
    refetch,
  } = useEntityDetailsQuery<SkuDTO>({
    parentKey: 'skus',
    id,
    isNew: !id,
    query: (fetchId, { signal }) =>
      productsGateway.getSku(fetchId, { includes: 'assemblies' }, { signal }),
  });

  const refresh = useCallback(async () => {
    await Promise.all([
      refetch(),
      queryClient.refetchQueries({ queryKey: ['inventories'], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['listings'], type: 'active' }),
    ]);
  }, [queryClient, refetch]);

  const { mutate: updateSku } = useGatewayMutation({
    mutationFn: (data: Partial<SkuDTO>) => productsGateway.updateSku(id!, data),
    linkedQuery: ['skus', id],
    successMessage: 'SKU updated',
  });

  const { mutate: updateBundleComponents } = useGatewayMutation({
    mutationFn: (data: Partial<SkuAssemblyComponentDTO>[]) =>
      productsGateway.updateAssemblyComponents(id!, data),
    linkedQuery: ['skus', id],
    successMessage: 'Bundle components updated',
  });

  const { mysteryComponents, deleteMysteryComponent, saveMysteryComponent } = useMysteryComponents(
    sku ?? {},
  );

  const { mutate: updateAliasSubstitutes } = useGatewayMutation({
    mutationFn: async (data: Partial<SkuAssemblyComponentDTO>[]) => {
      if (!id || !sku) throw new Error('No SKU available');

      const existingGroup = sku.componentGroups?.[0];
      let componentGroupId: string;

      if (existingGroup) {
        componentGroupId = existingGroup.id;
      } else {
        const group = await productsGateway.createSkuComponentGroup(id, {
          name: 'Substitutes',
          quantity: 1,
          selectionMethods: ['PRIORITY'],
        });
        componentGroupId = group.id;
      }

      return productsGateway.updateAssemblyComponents(
        id,
        data.map((component, index) => ({
          componentSkuId: component.componentSkuId,
          quantity: 1,
          componentGroupId,
          priority: index + 1,
        })),
      );
    },
    linkedQuery: ['skus', id],
    successMessage: 'Substitutes updated',
  });

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/products/skus" parentTitle="SKUs">
          <ShirtIcon />
        </HeaderIcon>
        <HeaderTitle>{sku?.name}</HeaderTitle>
        <HeaderDescription>{sku?.description}</HeaderDescription>
        <SkuActions sku={sku} skuId={id} onRefresh={refresh} />
      </Header>
      <PageContent variant="split">
        <DetailMain>
          {sku?.type === SkuType.MYSTERY ? (
            <MysteryComponentsCard
              sku={sku}
              mysteryComponents={mysteryComponents}
              isLoading={isLoading}
              onAddMysteryComponent={() => setEditingMysteryComponent('new')}
              onEditMysteryComponent={(index) =>
                setEditingMysteryComponent(mysteryComponents[index])
              }
              onDeleteMysteryComponent={deleteMysteryComponent}
            />
          ) : null}
          {sku?.type === SkuType.BUNDLE ? (
            <ComponentsTable
              components={sku.components ?? []}
              skuType={sku.type}
              title="Components"
              isLoading={isLoading}
              onUpdateComponents={updateBundleComponents}
            />
          ) : null}
          {sku?.type === SkuType.ALIAS ? (
            <ComponentsTable
              components={sku.components ?? []}
              skuType={sku.type}
              title="Substitutes"
              isLoading={isLoading}
              onUpdateComponents={updateAliasSubstitutes}
            />
          ) : null}
          {sku?.type === SkuType.STANDARD ? (
            <AssembliesCard sku={sku} isLoading={isLoading} />
          ) : null}
          <UnitsTable sku={sku} isLoading={isLoading} />
          <InventoryTable skuId={sku?.id ?? id} />
          <ListingsTable skuId={sku?.id ?? id} />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <DetailsSection sku={sku} isLoading={isLoading} onSubmit={updateSku} />
            <PricesSection sku={sku} isLoading={isLoading} onSubmit={updateSku} />
            <MeasurementsSection
              title="Measurements"
              sku={sku}
              isLoading={isLoading}
              onSubmit={updateSku}
            />
            <MeasurementsSection
              title="Verified measurements"
              measurement={sku?.verifiedMeasurement}
              isLoading={isLoading}
            />
          </DetailGroup>
        </DetailSide>
      </PageContent>

      <FormDialog
        open={editingMysteryComponent !== undefined}
        onOpenChange={(open) => !open && setEditingMysteryComponent(undefined)}
      >
        <MysteryComponentManagementDialog
          existingMysteryComponent={
            editingMysteryComponent === 'new' ? undefined : editingMysteryComponent
          }
          allAssemblyComponents={sku?.components ?? []}
          onSubmit={(newMysteryComponent) =>
            saveMysteryComponent({
              newMysteryComponent,
              existingMysteryComponent:
                editingMysteryComponent === 'new' ? undefined : editingMysteryComponent,
            })
          }
        />
      </FormDialog>
    </Page>
  );
}
